import { DollarSign, Activity, Wallet, MapPin, PackageSearch } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ROLES } from '../../constants';
import { sayfayiAcabilir } from '../../constants/sayfaRolleri';

/**
 * raporMenusu.ts
 *
 * Kenar çubuğunun rapor bağlantıları, TEK yerde. Rota 3.37'ye kadar bu bağlantılar
 * akordeon içinde üç ayrı JSX bloğu olarak elle yazılıydı ve rol koşulları yalnız
 * orada duruyordu; daraltılmış kenar çubuğundaki ikon ise koşullara hiç bakmadan
 * `/reports`e gidiyordu. Muhasebe o sayfayı açamıyor, bu yüzden ikona basınca
 * `ProtectedRoute` onu sessizce panoya atıyordu — mesaj yok, sebep yok.
 *
 * Tablo iki tarafı besler: akordeon bunu dolaşarak çizer, daraltılmış ikon da
 * `daraltilmisRaporHedefi` ile buradan hedef bulur. Yeni bir rapor bağlantısı
 * eklendiğinde iki taraf birlikte öğrenir.
 *
 * Buradaki `roles` "kime DOĞRUDAN bağlantı verilir" tercihidir, kimin açabildiği
 * değil. Açabilme bilgisi `constants/sayfaRolleri`nde; bağlantı oranın açmadığı bir
 * role verilemez (`raporMenusu.test.ts` denetler). Menü daha dar olabilir, geniş olamaz.
 *
 * KAPSAM DIŞI — bilerek: rapor KATEGORİLERİ (`REPORT_CATEGORIES`). Onlar admin ve
 * operasyona açık, veriden türüyor ve akordeonda ayrı bir döngüyle çiziliyor; iki
 * rol de `/reports`i zaten açabildiği için daraltılmış ikon onlara merkezi gösterir.
 */

export interface RaporBaglantisi {
  id: string;
  name: string;
  icon: LucideIcon;
  path: string;
  roles: string[];
}

// Sıra kenar çubuğunda göründükleri sıradır; daraltılmış ikon da ilk izinliyi seçer.
export const RAPOR_BAGLANTILARI: RaporBaglantisi[] = [
  {
    id: 'financial-revenue-breakdown',
    name: 'Ciro Kırılımı',
    icon: DollarSign,
    path: '/reports/financial/financial-revenue-breakdown',
    roles: [ROLES.TEKNIK, ROLES.MUHASEBE],
  },
  {
    id: 'teslimat-ciro',
    name: 'Teslimat Ciro',
    icon: Activity,
    path: '/reports/operational/teslimat-ciro',
    roles: [ROLES.TEKNIK],
  },
  {
    id: 'payment-tracking',
    name: 'Ödeme Takip',
    icon: Wallet,
    path: '/reports/payment-tracking',
    roles: [ROLES.ADMIN, ROLES.OPERASYON, ROLES.MUHASEBE],
  },
  // Rota 3.39: pazarlama rapor merkezini açamıyor, iki raporu doğrudan görür.
  // Admin ve operasyon aynı raporlara kategori sayfalarından ulaşıyor.
  {
    id: 'il-bazli-isi-haritasi',
    name: 'İl Bazlı Isı Haritası',
    icon: MapPin,
    path: '/reports/sales-analysis/il-bazli-isi-haritasi',
    roles: [ROLES.PAZARLAMA],
  },
  {
    id: 'product-performance',
    name: 'Ürün Performans',
    icon: PackageSearch,
    path: '/reports/product-analysis/product-performance',
    roles: [ROLES.PAZARLAMA],
  },
];

export const raporBaglantilari = (userRole: string): RaporBaglantisi[] =>
  RAPOR_BAGLANTILARI.filter((b) => b.roles.includes(userRole));

// Akordeonda rapor KATEGORİLERİ kime listelenir — menü tercihi (teknik kategori
// sayfasını açabildiği hâlde doğrudan bağlantı alıyor, 3.37). Kategori sayfasını
// açamayan role hiçbir koşulda listelenmez: menü rota kapısından geniş olamaz.
const KATEGORI_MENUSU_ROLLERI: string[] = [ROLES.ADMIN, ROLES.OPERASYON];

export const kategoriMenusuGorunur = (userRole: string): boolean =>
  KATEGORI_MENUSU_ROLLERI.includes(userRole) && sayfayiAcabilir('/reports/:categoryId', userRole);

/**
 * Daraltılmış kenar çubuğundaki rapor ikonunun gideceği yol.
 *
 * Rolün açabildiği bir sayfa döner; hiçbiri yoksa `null` — çağıran ikonu çizmez.
 * Kuralın tamamı: merkezi açabilen merkeze gider (kategoriler orada), açamayan kendi
 * ilk bağlantısına.
 */
export const daraltilmisRaporHedefi = (userRole: string): string | null => {
  if (sayfayiAcabilir('/reports', userRole)) return '/reports';
  return raporBaglantilari(userRole)[0]?.path ?? null;
};
